import { useMemo } from 'react';
import { ChoiceDrill } from './ChoiceDrill';
import type { DrillProps } from './meta';
import { makeSolveRound, type Graph } from './solve';

function RouteMap({ g }: { g: Graph }) {
  const X = (x: number) => 30 + x * 110, Y = (y: number) => 26 + y * 90;
  const at = (id: string) => g.nodes.find((n) => n.id === id)!;
  return (
    <svg className="route" viewBox="0 0 280 145" role="img" aria-label={`길 지도: ${g.edges.map((e) => `${e.a}-${e.b} ${e.w}분${e.blocked ? ' 막힘' : ''}`).join(', ')}`}>
      {g.edges.map((e) => {
        const a = at(e.a), b = at(e.b);
        const mx = (X(a.x) + X(b.x)) / 2, my = (Y(a.y) + Y(b.y)) / 2;
        return (
          <g key={`${e.a}${e.b}`}>
            <line x1={X(a.x)} y1={Y(a.y)} x2={X(b.x)} y2={Y(b.y)} className={e.blocked ? 'road blocked' : 'road'} />
            <rect x={mx - 11} y={my - 8} width="22" height="15" rx="4" className="road-tag" />
            <text x={mx} y={my + 4} textAnchor="middle" className="road-w">{e.w}</text>
          </g>
        );
      })}
      {g.nodes.map((n) => (
        <g key={n.id}>
          <circle cx={X(n.x)} cy={Y(n.y)} r="13" className={`stop ${n.id === 'A' || n.id === 'F' ? 'end' : ''}`} />
          <text x={X(n.x)} y={Y(n.y) + 5} textAnchor="middle" className="stop-id">{n.id}</text>
        </g>
      ))}
    </svg>
  );
}

export function SolveDrill({ level, seed, onDone }: DrillProps) {
  const questions = useMemo(() => makeSolveRound(level, seed), [level, seed]);
  return (
    <ChoiceDrill
      questions={questions} seed={seed} onDone={onDone} className="solve-drill"
      info={(q) => ({ kind: q.kind, ask: q.ask })}
      render={(q) => (
        <div className="lang-q">
          <div className="read-box">
            {q.lines.map((l) => <p key={l}>{l}</p>)}
            {q.table && (
              <table className="data-table">
                <thead><tr>{q.table.head.map((h) => <th key={h}>{h}</th>)}</tr></thead>
                <tbody>{q.table.rows.map((row) => <tr key={row[0]}>{row.map((c, i) => (i === 0 ? <th key={i} scope="row">{c}</th> : <td key={i}>{c}</td>))}</tr>)}</tbody>
              </table>
            )}
            {q.graph && <RouteMap g={q.graph} />}
          </div>
          <p className="lang-ask small-ask">{q.ask}</p>
        </div>
      )}
      renderChoice={(q, i) => <span className="choice-text">{q.options[i]}</span>}
    />
  );
}
